import Debug from '../utils/Debug.js';

export default class SfxManager {
    /**
     * Lightweight SFX helper that reuses an existing AudioContext.
     * Responsible only for:
     *   - Decoding and caching AudioBuffers by URL
     *   - Playing one-shot sounds with a click-safe envelope
     *
     * Category-to-URL mapping and bus routing are owned by AudioManager.
     * Ported from SS2 — uses fetch() instead of assetCache.
     */
    constructor(audioContext) {
        this.audioContext = audioContext || null;
        /** @type {Map<string, AudioBuffer>} */
        this._bufferCache = new Map();
        /** @type {Map<string, Promise<AudioBuffer|null>>} */
        this._loadingCache = new Map();
    }

    /**
     * Load (and cache) an AudioBuffer for the given URL.
     * Returns null if loading or decoding fails.
     * @param {string} url
     * @returns {Promise<AudioBuffer|null>}
     */
    async loadBuffer(url) {
        if (!this.audioContext || !url) return null;

        if (this._bufferCache.has(url)) {
            return this._bufferCache.get(url);
        }
        if (this._loadingCache.has(url)) {
            return this._loadingCache.get(url);
        }

        const task = (async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
                const arrayBuf = await response.arrayBuffer();
                const buf = await this.audioContext.decodeAudioData(arrayBuf);
                if (buf) {
                    this._bufferCache.set(url, buf);
                    return buf;
                }
            } catch (e) {
                Debug.warn('SfxManager', 'Error loading/decoding SFX buffer at', url, e);
            } finally {
                this._loadingCache.delete(url);
            }
            return null;
        })();

        this._loadingCache.set(url, task);
        return task;
    }

    /**
     * Play a single one-shot sound with a gentle attack/release envelope.
     *
     * @param {Object} opts
     * @param {string} opts.url - Audio file URL (relative to server root, e.g. "audio/sfx/ui/ui_click_light_01.wav")
     * @param {AudioNode} opts.destination - Destination node (typically a bus GainNode)
     * @param {number} [opts.gain=1.0] - Linear gain applied on a per-playback GainNode
     * @param {number} [opts.playbackRate=1.0] - Base playback rate
     * @param {number} [opts.playbackRateJitter=0.0] - Symmetric random jitter (e.g. 0.02 => ±2%)
     * @param {number} [opts.attack=0.005] - Attack time in seconds
     * @param {number} [opts.release=0.04] - Release time in seconds
     * @param {number|null} [opts.maxDuration=null] - Optional hard cap on playback duration
     */
    async playOneShot(opts) {
        const ac = this.audioContext;
        if (!ac || !opts || !opts.url || !opts.destination) return;

        const {
            url,
            destination,
            gain = 1.0,
            playbackRate = 1.0,
            playbackRateJitter = 0.0,
            attack = 0.005,
            release = 0.04,
            maxDuration = null
        } = opts;

        const buffer = await this.loadBuffer(url);
        if (!buffer) return;

        let source;
        let envelope;
        try {
            source = ac.createBufferSource();
            envelope = ac.createGain();

            source.buffer = buffer;

            // Slight random pitch variation for variation, if requested
            let rate = playbackRate;
            if (playbackRateJitter > 0) {
                const r = (Math.random() * 2 - 1) * playbackRateJitter; // [-jitter, +jitter]
                rate *= (1 + r);
            }
            source.playbackRate.value = rate;

            // Envelope: small non-zero start time to avoid scheduling at t == currentTime
            const now = ac.currentTime;
            const startTime = now + 0.002;
            const attackTime = Math.max(0.001, attack);
            const releaseTime = Math.max(0.005, release);

            envelope.gain.setValueAtTime(0, startTime);
            envelope.gain.linearRampToValueAtTime(gain, startTime + attackTime);

            let naturalDuration = buffer.duration;
            if (typeof maxDuration === 'number' && maxDuration > 0) {
                naturalDuration = Math.min(naturalDuration, maxDuration);
            }

            const endTime = startTime + naturalDuration;
            const releaseStart = Math.max(startTime + attackTime * 0.5, endTime - releaseTime);

            envelope.gain.setValueAtTime(gain, releaseStart);
            envelope.gain.linearRampToValueAtTime(0.0001, releaseStart + releaseTime);

            source.connect(envelope);
            envelope.connect(destination);

            source.start(startTime);
            // Stop slightly after release to allow envelope to reach zero
            source.stop(releaseStart + releaseTime + 0.02);

            // Explicitly disconnect nodes after playback to help GC.
            // Without this, the browser may retain audio graph references
            // indefinitely, causing OOM over long sessions (8+ hours).
            source.onended = () => {
                source.disconnect();
                envelope.disconnect();
                source.onended = null;
            };
        } catch (e) {
            Debug.warn('SfxManager', 'playOneShot failed for', url, e);
            try {
                if (source) { source.disconnect(); source.stop(); }
                if (envelope) envelope.disconnect();
            } catch {
                // ignore
            }
        }
    }

    /**
     * Clear all cached buffers. Intended for rare full reloads.
     */
    clearCache() {
        this._bufferCache.clear();
        this._loadingCache.clear();
    }
}
